import { ArrowRight } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useLocation } from "react-router-dom";
import { ApiError, api } from "../api";
import { useAuth } from "../auth";
import { AuthPage } from "./AuthPage";
import { NoirBar, NoirFooter } from "./NoirChrome";
import "./MarketingPage.css";
import "./LegalPage.css";
import "./OAuthConsentPage.css";

// A hosted connector (Claude.ai, ChatGPT) sent the person here from /oauth/authorize with the original
// query intact. The worker already validated the client and its redirect URI; this page asks which
// workspace the connector may see, with which scopes, then posts the decision and follows the redirect.

type Scope = "read" | "report" | "manage";
type ConsentInfo = { client: { id: string; name: string; redirectHost: string; clientUri: string | null; logoUri: string | null }; scopes: Scope[] };

const SCOPE_COPY: Record<Scope, { label: string; detail: string }> = {
  read: { label: "Read", detail: "See loops, runs, and diagnostics in the workspace." },
  report: { label: "Report", detail: "Send run outcomes, notes, definitions, and dependencies." },
  manage: { label: "Manage", detail: "Create loops, share them, invite people, and move loops between agents." },
};
const SCOPE_ORDER: Scope[] = ["read", "report", "manage"];
const RETURN_KEY = "lg.oauthReturn";
const manageRoles = ["owner", "admin"];

export function OAuthConsentPage() {
  const { user, loading, organizations } = useAuth();
  const location = useLocation();
  const query = location.search;

  useEffect(() => {
    document.title = "Connect to LoopGroup";
  }, []);

  // Signed out: remember where to come back to, then show the ordinary sign-in and return afterwards.
  useEffect(() => {
    if (!user && !loading && query) {
      try {
        sessionStorage.setItem(RETURN_KEY, `${location.pathname}${query}`);
      } catch {
        // Storage can be unavailable; the from state below still carries the return path.
      }
    }
  }, [user, loading, query, location.pathname]);

  if (loading) return <div className="noir legal consent"><NoirBar /><main className="consent__main"><div className="consent__status" role="status"><div className="boot__loop" /><span>Checking your session…</span></div></main><NoirFooter /></div>;
  if (!user) return <AuthPage mode="signin" returnTo={`${location.pathname}${query}`} />;
  return <ConsentForm query={query} organizations={organizations} />;
}

function ConsentForm({ query, organizations }: { query: string; organizations: Array<{ id: string; name: string; kind: "personal" | "group"; role: string }> }) {
  const [info, setInfo] = useState<ConsentInfo | null>(null);
  const [loadError, setLoadError] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [busy, setBusy] = useState<"approve" | "deny" | null>(null);
  const [error, setError] = useState("");

  const eligible = useMemo(() => organizations.filter((organization) => manageRoles.includes(organization.role)), [organizations]);
  const original = useMemo(() => Object.fromEntries(new URLSearchParams(query)) as Record<string, string>, [query]);

  useEffect(() => {
    let cancelled = false;
    if (!query) {
      setLoadError("This page needs a connection request from a connector. Add LoopGroup as a connector in your agent and it will send you here.");
      return;
    }
    api<ConsentInfo>(`/api/oauth/consent${query}`)
      .then((data) => {
        if (cancelled) return;
        setInfo(data);
        setScopes(data.scopes);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setLoadError(caught instanceof ApiError ? caught.message : "This connection request could not be read.");
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  useEffect(() => {
    if (!organizationId && eligible[0]) setOrganizationId(eligible[0].id);
  }, [eligible, organizationId]);

  async function decide(decision: "approve" | "deny", event?: FormEvent) {
    event?.preventDefault();
    if (!info) return;
    setBusy(decision);
    setError("");
    try {
      const result = await api<{ redirect: string }>("/api/oauth/consent", {
        method: "POST",
        body: JSON.stringify({ ...original, organizationId: organizationId || eligible[0]?.id, scopes: decision === "approve" ? scopes : info.scopes, decision }),
      });
      try {
        sessionStorage.removeItem(RETURN_KEY);
      } catch {
        // Nothing to clear.
      }
      window.location.assign(result.redirect);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "The decision could not be recorded. Nothing was connected.");
      setBusy(null);
    }
  }

  const canApprove = Boolean(info) && scopes.length > 0 && Boolean(organizationId) && busy === null;

  return (
    <div className="noir legal consent">
      <NoirBar pill={{ to: "/app", label: "Open LoopGroup" }} />
      <main className="consent__main">
        {loadError && (
          <header className="consent__head">
            <p className="noir__eyebrow">Connection request</p>
            <h1 className="consent__title">This request cannot continue.</h1>
            <p className="consent__lede">{loadError}</p>
          </header>
        )}
        {!loadError && !info && <div className="consent__status" role="status"><div className="boot__loop" /><span>Reading the connection request…</span></div>}
        {info && (
          <>
            <header className="consent__head">
              <p className="noir__eyebrow">Connection request</p>
              <h1 className="consent__title">Connect <em>{info.client.name}</em> to LoopGroup</h1>
              <p className="consent__lede">{info.client.name} wants to use the LoopGroup MCP server on your behalf. It receives a scoped key you can revoke from Settings at any time; it never sees your passkey.</p>
              <p className="consent__return"><span>Returns to</span><code>{info.client.redirectHost}</code></p>
            </header>

            <form className="consent__form" onSubmit={(event) => void decide("approve", event)}>
              <section className="consent__section" aria-labelledby="consent-workspace">
                <h2 id="consent-workspace">Workspace</h2>
                <p>{eligible.length === 0 ? "You need to be an owner or admin of a workspace to connect an agent to it." : "The connector sees this workspace only. Personal first; groups you administer follow."}</p>
                <ul className="consent__workspaces">
                  {organizations.map((organization) => {
                    const allowed = manageRoles.includes(organization.role);
                    const selected = organization.id === organizationId;
                    return (
                      <li key={organization.id}>
                        <label className={`consent__workspace ${selected ? "is-selected" : ""} ${allowed ? "" : "is-readonly"}`}>
                          <input type="radio" name="organizationId" value={organization.id} checked={selected} disabled={!allowed} onChange={() => setOrganizationId(organization.id)} />
                          <span><strong>{organization.name}</strong><small>{allowed ? `You are ${organization.role === "owner" ? "the owner" : "an admin"}` : `Your ${organization.role} role cannot connect agents`}</small></span>
                          <span className="consent__kind">{organization.kind === "personal" ? "personal" : "group"}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </section>

              <section className="consent__section" aria-labelledby="consent-scopes">
                <h2 id="consent-scopes">Access</h2>
                <p>Grant only what the connector needs. Each scope is a separate, revocable authority on one key.</p>
                <div className="consent__scopes">
                  {SCOPE_ORDER.map((scope) => {
                    const checked = scopes.includes(scope);
                    return (
                      <label key={scope} className={`consent__scope ${checked ? "is-selected" : ""}`}>
                        <input type="checkbox" name="scopes" value={scope} checked={checked} onChange={(event) => setScopes(event.target.checked ? SCOPE_ORDER.filter((candidate) => candidate === scope || scopes.includes(candidate)) : scopes.filter((candidate) => candidate !== scope))} />
                        <span><b>{SCOPE_COPY[scope].label}</b><small>{SCOPE_COPY[scope].detail}</small></span>
                        <span className="consent__scope-name">{scope}</span>
                      </label>
                    );
                  })}
                </div>
              </section>

              {error && <div className="consent__error" role="alert">{error}</div>}

              <div className="consent__actions">
                <button type="submit" className="noir__cta consent__approve" disabled={!canApprove}>{busy === "approve" ? "Connecting…" : "Approve"} <ArrowRight size={18} aria-hidden="true" /></button>
                <button type="button" className="consent__deny" disabled={busy !== null} onClick={() => void decide("deny")}>{busy === "deny" ? "Declining…" : "Deny"}</button>
                <p className="consent__boundary">Approving mints one key named "{info.client.name} connector" in the chosen workspace and records it in Activity. Nothing is shared with anyone else, and no loop is changed.</p>
              </div>
            </form>
          </>
        )}
      </main>
      <NoirFooter />
    </div>
  );
}
